import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { AGENTS_ENGINE_MODULE_KEY } from 'src/app/shared/constants/agents-engine-access';
import type { User } from 'src/app/shared/model/user.model';
import { UserSettingsService } from 'src/app/shared/services/user-setting.service';
import { OrganizationChanges, orgScopedKey } from './org-scope';
import { hasAnyScope, scopesOf, type ApiScope } from './scopes';

/** Margen con el que un token se da por caducado antes de su `exp` (no se envía uno a punto de expirar). */
export const EXPIRY_SKEW_MS = 5_000;
/** Tope de `setTimeout` (2^31 − 1 ms ≈ 24,8 días): un `exp` más lejano se reevalúa al vencer el tope. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** Clave de `localStorage` donde Sirius guarda el JWT (`UserSettingsService.setUser`). */
export const TOKEN_STORAGE_KEY = 'token';
/** Clave de `localStorage` donde Sirius guarda la instantánea `User` del JWT. */
export const USER_STORAGE_KEY = 'User';

/** Identidad derivada de la sesión de Sirius: lo que la feature muestra y con lo que decide. */
export interface SiriusIdentity {
  userId: number;
  email: string;
  fullName: string | null;
  organizationsId: number;
  organizationName: string | null;
  access: readonly string[];
  scopes: readonly ApiScope[];
  /** La organización tiene el módulo `agents-engine` en `orgModules` (sin él la API responde 403 en toda ruta). */
  moduleEnabled: boolean;
}

/**
 * Por qué la sesión de Sirius no sirve (todavía) para Agentes IA; null cuando sirve. `no-organization` es lo que
 * la API respondería con 401 «no active organization»; `module-disabled`, su 403 «module not enabled».
 */
export type SessionIssue = 'no-session' | 'no-organization' | 'module-disabled';

/** `exp` del JWT en milisegundos de época; null sin token, sin `exp` o si no es un JWT. */
export function tokenExpiresAt(token: string | null): number | null {
  if (!token) return null;
  try {
    const exp = jwtDecode<{ exp?: number }>(token).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Vigente = con `exp` en el futuro (o sin `exp`); `skewMs` adelanta la caducidad para no enviar un token a punto de expirar. */
export function isTokenAlive(expiresAt: number | null, now = Date.now(), skewMs = 0): boolean {
  return expiresAt === null || expiresAt - skewMs > now;
}

/**
 * Identidad del `User` que Sirius guarda en `localStorage['User']` (instantánea del JWT). Null sin sesión, sin
 * token o sin organización activa (la API respondería 401 «no active organization»). `User.access` está tipado
 * `Access[]` en `user.model.ts` pero el token trae `string[]` (así lo trata `PermissionService`).
 */
export function identityOf(user: User | null, token: string | null): SiriusIdentity | null {
  if (!user || !token) return null;
  const organization = user.currentLoggedOrganization;
  const organizationsId = Number(organization?.id);
  if (!organization || !Number.isSafeInteger(organizationsId) || organizationsId < 1) return null;
  const access = ((user.access ?? []) as unknown[]).filter(
    (entry): entry is string => typeof entry === 'string',
  );
  const fullName =
    (typeof user.userFullName === 'string' ? user.userFullName.trim() : '') ||
    [user.firstName, user.lastName]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map(part => part.trim())
      .join(' ');
  const organizationName = typeof organization.name === 'string' ? organization.name.trim() : '';
  return {
    userId: user.id,
    email: user.email,
    fullName: fullName || null,
    organizationsId,
    organizationName: organizationName || null,
    access,
    scopes: scopesOf(access),
    moduleEnabled:
      Array.isArray(user.orgModules) &&
      user.orgModules.some(module => module?.key === AGENTS_ENGINE_MODULE_KEY),
  };
}

/** Diagnóstico de la sesión para el aviso del shell; null si la sesión sirve. */
export function sessionIssueOf(
  user: User | null,
  token: string | null,
  identity: SiriusIdentity | null,
): SessionIssue | null {
  if (!user || !token) return 'no-session';
  if (!identity) return 'no-organization';
  if (!identity.moduleEnabled) return 'module-disabled';
  return null;
}

/**
 * La identidad de la feature es la sesión de Sirius (diseño F): el JWT de `UserSettingsService` (`localStorage`
 * `token`) y su instantánea `User`. Es un adaptador con la interfaz de signals que tenía la consola (`token`, `me`,
 * `isAuthenticated`, `orgId`, `access`, `scopes` —`scopes.ts`, tabla E.3—, `email`, `actorLabel`, `expiresAt`,
 * `expired`; `hasScope`, `missingScope`, `scopedKey`, `handleUnauthorized`), sin puente `postMessage`,
 * `sessionStorage`, login ni logout propios (el logout es el de la cabecera de Sirius).
 *
 * Global (desde que algo lo inyecta) solo es el refresco de signals: `userChanges$` (en un microtask: `setUser`
 * emite ANTES de escribir `localStorage['token']`), `loggedOut$` y el evento `storage` de otra pestaña (token
 * nuevo, otra organización). Un cambio de organización avisa por `OrganizationChanges` (cachés y streams se
 * vacían). `expiresAt`/`expired` salen del `exp` del token, haya o no organización activa.
 *
 * Lo que cierra la sesión por iniciativa propia solo actúa mientras «Agentes IA» está montada: el shell llama a
 * `watchSession(destroyRef)` y, hasta que se destruye, un temporizador cierra la sesión de Sirius a `exp − 5 s`
 * (Sirius solo comprueba `exp` en cada petición o navegación: una pantalla ociosa con un stream SSE abierto no se
 * enteraría) y la desaparición del token en otra pestaña también la cierra (`logOut(false)`, como hacía el host
 * del iframe). Fuera de la feature Sirius se comporta como siempre. Un 401 del motor (`handleUnauthorized`, que
 * solo llega desde peticiones del motor) también cierra la sesión.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userSettings = inject(UserSettingsService);
  private readonly organizationChanges = inject(OrganizationChanges);

  private readonly tokenSignal = signal<string | null>(null);
  private readonly identitySignal = signal<SiriusIdentity | null>(null);
  private readonly issueSignal = signal<SessionIssue | null>('no-session');
  private readonly expiredSignal = signal(false);
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Cuántos `watchSession` siguen activos (el shell de la feature; uno en la práctica). */
  private watchers = 0;
  /** Última organización con sesión (sobrevive al cierre: entrar luego en otra organización también vacía las cachés). */
  private lastOrgId: number | null = null;
  private closingSession = false;

  /** El JWT de Sirius en sesión (solo lo leen el interceptor y el cliente SSE). */
  readonly token = this.tokenSignal.asReadonly();
  /** Identidad derivada de la sesión; null sin sesión o sin organización activa. */
  readonly me = this.identitySignal.asReadonly();
  readonly isAuthenticated = computed(() => this.identitySignal() !== null);
  /** Qué le falta a la sesión para servir (sin sesión, sin organización, sin módulo); null si sirve. */
  readonly sessionIssue = this.issueSignal.asReadonly();
  readonly orgId = computed(() => this.identitySignal()?.organizationsId ?? null);
  readonly access = computed<readonly string[]>(() => this.identitySignal()?.access ?? []);
  readonly scopes = computed<readonly ApiScope[]>(() => this.identitySignal()?.scopes ?? []);
  readonly email = computed(() => this.identitySignal()?.email ?? null);
  /** Identidad del libro mayor (`created_by`, `decided_by`…): `user:<id>`. */
  readonly actorLabel = computed(() => {
    const me = this.identitySignal();
    return me ? `user:${me.userId}` : null;
  });
  /** Caducidad del token en sesión (`exp`), con o sin organización activa; null sin token o sin `exp`. */
  readonly expiresAt = computed(() => {
    const at = tokenExpiresAt(this.tokenSignal());
    return at === null ? null : new Date(at);
  });
  /**
   * El token en sesión ya había caducado al leerlo (con el margen de 5 s), o la sesión se cerró por caducidad o
   * por un 401. Vuelve a `false` con un token vigente.
   */
  readonly expired = this.expiredSignal.asReadonly();

  private readonly onStorage = (event: StorageEvent): void => this.storageChanged(event);

  constructor() {
    this.refresh();
    const userChanges = this.userSettings.userChanges$.subscribe(() => {
      // `setUser` emite antes de escribir `localStorage['token']`: se lee en el siguiente microtask.
      queueMicrotask(() => this.refresh());
    });
    const loggedOut = this.userSettings.loggedOut$.subscribe(() => this.refresh());
    if (typeof window !== 'undefined') window.addEventListener('storage', this.onStorage);
    inject(DestroyRef).onDestroy(() => {
      userChanges.unsubscribe();
      loggedOut.unsubscribe();
      if (typeof window !== 'undefined') window.removeEventListener('storage', this.onStorage);
      this.clearExpiryTimer();
    });
  }

  /** Si la sesión tiene alguno de los scopes (`admin` abre todo). */
  hasScope(...required: ApiScope[]): boolean {
    return hasAnyScope(this.scopes(), required);
  }

  /** El primer scope de `required` que falta, para el tooltip «Necesita el acceso …»; null si puede. */
  missingScope(...required: ApiScope[]): ApiScope | null {
    return this.hasScope(...required) ? null : (required[0] ?? null);
  }

  /** Clave de almacenamiento del navegador propia de la organización en sesión (`org:<id>:<key>`). */
  scopedKey(key: string): string {
    const orgId = this.orgId();
    return orgId === null ? key : orgScopedKey(orgId, key);
  }

  /**
   * Vigila la sesión mientras la feature está montada: el shell lo llama en su constructor con su `DestroyRef`
   * y la vigilancia se retira al destruirse. Arma el temporizador de caducidad (`exp − 5 s` → `logOut(false)`)
   * y el cierre de sesión cuando otra pestaña borra el token. Admite llamadas anidadas (se retira con la última).
   */
  watchSession(destroyRef: DestroyRef): void {
    this.watchers += 1;
    if (this.watchers === 1) this.scheduleExpiry();
    let active = true;
    destroyRef.onDestroy(() => {
      if (!active) return;
      active = false;
      this.watchers -= 1;
      if (this.watchers === 0) this.clearExpiryTimer();
    });
  }

  /**
   * Política del 401 (interceptor y SSE, salvo rutas de operador): el token dejó de valer para la API; se cierra
   * la sesión de Sirius. Sin token en sesión no hay nada que cerrar.
   */
  handleUnauthorized(): void {
    if (this.tokenSignal() === null) return;
    this.expire();
  }

  /**
   * Otra pestaña cambió la sesión. Siempre: se releen las signals (un token nuevo, otra organización, el nombre
   * de la organización actualizado). Solo con la feature montada: si el token ya no está (logout en otra
   * pestaña, `localStorage.clear()` → `key === null`), se cierra también esta sesión de Sirius. Se mira el
   * almacenamiento y no la clave del evento: `logOut` borra `User` y `token` en la misma tarea, así que cuando
   * llega el evento de `User` el token ya no existe (`closeSession` evita el segundo cierre).
   */
  private storageChanged(event: StorageEvent): void {
    const { key } = event;
    if (key !== null && key !== TOKEN_STORAGE_KEY && key !== USER_STORAGE_KEY) return;
    this.refresh();
    if (this.watchers > 0 && this.tokenSignal() === null) this.closeSession();
  }

  /** Relee la sesión de Sirius, reprograma la caducidad si cambió el token y avisa si cambió la organización (o se cerró la sesión). */
  private refresh(): void {
    const token = this.userSettings.getToken() || null;
    const user = this.userSettings.getUser();
    const identity = identityOf(user, token);
    const previous = this.identitySignal();
    const tokenChanged = token !== this.tokenSignal();
    this.tokenSignal.set(token);
    this.identitySignal.set(identity);
    this.issueSignal.set(sessionIssueOf(user, token, identity));
    if (identity) {
      if (this.lastOrgId !== null && this.lastOrgId !== identity.organizationsId) {
        this.organizationChanges.notify();
      }
      this.lastOrgId = identity.organizationsId;
    } else if (previous) {
      this.organizationChanges.notify();
    }
    if (!tokenChanged) return;
    if (token !== null) {
      this.closingSession = false;
      this.expiredSignal.set(!isTokenAlive(tokenExpiresAt(token), Date.now(), EXPIRY_SKEW_MS));
    }
    this.scheduleExpiry();
  }

  /**
   * Con la feature montada (`watchSession`) y un token con `exp`: `exp − 5 s` → `expire()`. Un `exp` más allá
   * del tope de `setTimeout` se reevalúa al vencer el tope. Sin vigilancia o sin token no hay temporizador.
   */
  private scheduleExpiry(): void {
    this.clearExpiryTimer();
    const expiresAt = tokenExpiresAt(this.tokenSignal());
    if (this.watchers === 0 || expiresAt === null) return;
    const delay = Math.min(Math.max(0, expiresAt - EXPIRY_SKEW_MS - Date.now()), MAX_TIMEOUT_MS);
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = null;
      if (this.tokenSignal() === null) return;
      if (isTokenAlive(tokenExpiresAt(this.tokenSignal()), Date.now(), EXPIRY_SKEW_MS)) {
        this.scheduleExpiry();
        return;
      }
      this.expire();
    }, delay);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
  }

  /** Caducidad o 401: se recuerda (`expired`) y se cierra la sesión de Sirius. */
  private expire(): void {
    this.expiredSignal.set(true);
    this.closeSession();
  }

  /** `logOut(false)` una sola vez por cierre: corta la reentrada vía `userChanges$`/`loggedOut$`/`storage`. */
  private closeSession(): void {
    if (this.closingSession) return;
    this.closingSession = true;
    this.clearExpiryTimer();
    this.userSettings.logOut(false);
  }
}
